import { getFontCss, styleConstants } from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectBinRangeInfoFromState,
  selectFetchingIssuerFromState,
  selectTopLevelErrorFromState,
} from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import {
  AccountBinInputPageCvcFlatFields,
  AccountBinInputPageFlatFields,
} from 'src/app/RegisterAccount/RegisterAccount'
import RegisterAccountBinInputForm from 'src/components/RegisterAccountBinInputForm/RegisterAccountBinInputForm'
import SvgCheckLoD from 'src/components/Svgs/SvgCheckLoD'
import {
  AstradaBinData,
  calculateAstradaEligibility,
} from 'src/context/AstradaContext/AstradaContext'
import useAstrada from 'src/context/AstradaContext/useAstrada'
import { IssuerSchemaFilterFields } from 'src/enums'
import Trns, { useTranslate } from 'src/localization/Trns'
import { connectedPush } from 'src/navigation/Lnk'
import { registerAccountUrl } from 'src/navigation/urls'
import { getIssuerFromBin } from 'src/redux/actions/data/creditCards.actions'
import { getIssuers } from 'src/redux/actions/data/issuerIntegrators.actions'
import { selectIssuerIntegratorFromState } from 'src/redux/selectors/data/issuerIntegrators.selectors'
import { css } from 'styled-components'

interface OnboardingStepThreeProps {
  currentStep: number
  totalSteps: number
  setStep: (val: number) => void
  flatFields: AccountBinInputPageFlatFields
  onFlatFieldChange: (
    fieldName: keyof AccountBinInputPageFlatFields,
    value: AccountBinInputPageFlatFields[keyof AccountBinInputPageFlatFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<
      keyof AccountBinInputPageFlatFields,
      Array<React.ReactElement | string>
    >
  >
  setErrorsAndReturnDidPassValidation: () => boolean
  cvcFlatFields: AccountBinInputPageCvcFlatFields
  onCvcFlatFieldChange: (
    fieldName: keyof AccountBinInputPageCvcFlatFields,
    value: AccountBinInputPageCvcFlatFields[keyof AccountBinInputPageCvcFlatFields],
  ) => void
  cvcFlatFieldsErrors: Partial<
    Record<
      keyof AccountBinInputPageCvcFlatFields,
      Array<React.ReactElement | string>
    >
  >
  cvcSetErrorsAndReturnDidPassValidation: () => boolean
  title: string
  subtitle: React.ReactNode
  cardInfoHeader: React.ReactElement
  cardNumberPlaceholder?: string
  expirationDatePlaceholder?: string
  cvcPlaceholder?: string
}

const services = [
  'Send secure virtual cards* to vendors, employees, and contractors',
  'Organize expenses for different teams, projects, clients, or locations',
  'Capture and auto-match receipts',
  'Automate expense tracking and reconciliation',
  'Sync transaction data to your accounting software',
]

const OnboardingStepThree: React.FC<OnboardingStepThreeProps> = (props) => {
  const fetchingIssuer = useSelector(selectFetchingIssuerFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const binRangeInfo = useSelector(selectBinRangeInfoFromState)
  const selectedIssuer = useSelector(selectIssuerIntegratorFromState)
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const astrada = useAstrada()

  const stepThreeSideInfo = (
    <div
      css={css`
        border-radius: 0px 10px 10px 0px;
        max-width: 320px;
        background: linear-gradient(27deg, #4f3fdd 0.27%, #588af2 100.75%);
        box-shadow: 4px 0 8px 0 rgba(0, 0, 0, 0.15) inset;
      `}
    >
      <div
        css={css`
          margin: 120px 40px;
        `}
      >
        <div
          css={css`
            ${getFontCss(
              20,
              styleConstants.colors.white,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 16px;
          `}
        >
          {translate('Do more with your company credit card')}
        </div>
        <div
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.white,
              styleConstants.fontFamilies.regular,
            )};
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          {services.map((service) => (
            <div
              key={service}
              css={css`
                display: flex;
                flex-direction: row;
                gap: 8px;
              `}
            >
              <SvgCheckLoD
                css={css`
                  align-self: flex-start;
                  margin-top: -4px;
                `}
              />
              <span
                css={css`
                  max-width: 212px;
                `}
              >
                {translate(service)}
              </span>
            </div>
          ))}
          <div
            css={css`
              margin-top: 144px;
              font-size: 12px;
              font-style: italic;
              line-height: 16px;
            `}
          >
            {translate(
              '*Virtual card capabilities available through select bank partners.',
            )}
          </div>
        </div>
      </div>
    </div>
  )

  const dispatch = useDispatch()

  const cvcRequired =
    selectedIssuer?.schema !== undefined &&
    selectedIssuer.schema.filter(
      (field) => field.name === IssuerSchemaFilterFields.cvc,
    ).length > 0

  useEffect((): void => {
    dispatch(getIssuers())
  }, [])

  // need this for CVC field validation
  useEffect((): void => {
    // assumes getBinRange was successful
    if (binRangeInfo) {
      const { partnerEnabled, astradaEnabled, ...rest } = binRangeInfo
      const eligibility = calculateAstradaEligibility({
        partnerEnabled,
        astradaEnabled,
      })
      const astradaBinData: AstradaBinData = {
        ...rest,
        astradaEnabled,
        eligibility,
        issuerLogoImg: selectedIssuer?.url,
        issuerRequiresVerification: selectedIssuer?.needsCardholderVerification,
      }
      const { cardNumber, expirationDate } = props.flatFields
      const cvc = props.cvcFlatFields.cvc

      // All 3 of these fields must be truthy in order for this next section to work
      const ccDataValid = !!(cardNumber && cvc && expirationDate)

      // If the push() event happens, the entered card is valid
      if (cvcRequired) {
        if (props.cvcSetErrorsAndReturnDidPassValidation()) {
          if (ccDataValid) {
            astrada.updateState({
              binData: astradaBinData,
              cardData: {
                number: cardNumber,
                cvc,
                expiration: expirationDate,
              },
              onboarding: true,
            })
          }
          dispatch(
            connectedPush({
              pathname: registerAccountUrl(),
              search: '',
              state: {
                currentPage: 2,
                accountBinInputPageFlatFields: props.flatFields,
                accountBinInputPageCvcFlatFields: props.cvcFlatFields,
              },
              hash: '',
            }),
          )
        }
      } else {
        if (ccDataValid) {
          astrada.updateState({
            binData: astradaBinData,
            cardData: {
              number: cardNumber,
              cvc,
              expiration: expirationDate,
            },
            onboarding: true,
          })
        }
        dispatch(
          connectedPush({
            pathname: registerAccountUrl(),
            search: '',
            state: {
              currentPage: 2,
              accountBinInputPageFlatFields: props.flatFields,
              accountBinInputPageCvcFlatFields: props.cvcFlatFields,
            },
            hash: '',
          }),
        )
      }
    }
  }, [binRangeInfo])

  const onAccountBinInputPageSubmit = (bin?: string): void => {
    analytics?.track('register card - continue', {
      intent: 'continue',
      interaction: 'button press',
    })

    const accountBinInputPageFlatFieldsPassedValidation =
      props.setErrorsAndReturnDidPassValidation()
    if (bin && accountBinInputPageFlatFieldsPassedValidation) {
      dispatch(
        getIssuerFromBin(
          { bin: bin.replace(/\s/g, '').substring(0, 10) },
          false,
          analytics,
        ),
      )
      // next step in logic is in the binRangeInfo useEffect
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-grow: 1;
      `}
    >
      <div
        css={css`
          flex-grow: 1;
        `}
      >
        <RegisterAccountBinInputForm
          css={css`
            padding: 80px;
          `}
          flatFields={props.flatFields}
          onFlatFieldChange={props.onFlatFieldChange}
          flatFieldsErrors={props.flatFieldsErrors}
          cvcFlatFields={props.cvcFlatFields}
          onCvcFlatFieldChange={props.onCvcFlatFieldChange}
          cvcFlatFieldsErrors={props.cvcFlatFieldsErrors}
          loading={fetchingIssuer}
          onSubmit={onAccountBinInputPageSubmit}
          stepElement={
            <div
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.purple,
                  styleConstants.fontFamilies.regular,
                )};
                margin-bottom: 20px;
              `}
            >
              <Trns
                source={'Step ${currentStep} of ${totalSteps}'}
                expressions={{
                  currentStep: props.currentStep.toString(),
                  totalSteps: props.totalSteps.toString(),
                }}
              />
            </div>
          }
          title={translate('Add your company credit card')}
          subtitle={props.subtitle}
          cardInfoHeader={props.cardInfoHeader}
          boldFieldHeaders
          cardNumberPlaceholder={props.cardNumberPlaceholder}
          expirationDatePlaceholder={props.expirationDatePlaceholder}
          cvcPlaceholder={props.cvcPlaceholder}
          submitButtonText={<Trns source={'Continue'} />}
          topLevelError={topLevelError}
        />
      </div>
      {stepThreeSideInfo}
    </div>
  )
}

export default OnboardingStepThree
